import {
  TriggerForm,
  TriggerFormValues,
} from "@/components/knowledge-base/trigger-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useGetTriggerById,
  useUpdateTrigger,
} from "@/lib/api/triggers-knowledge-base";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute(
  "/_authenticated/knowledge-base/$triggerKnowledgeBaseId",
)({
  component: EditTrigger,
});

function EditTrigger() {
  const params = Route.useParams();
  const navigate = useNavigate();

  const { data: trigger, isLoading } = useGetTriggerById(
    params.triggerKnowledgeBaseId,
  );
  const { mutateAsync: updateTrigger, isPending: isUpdating } =
    useUpdateTrigger();

  const handleSubmit = async (values: Partial<TriggerFormValues>) => {
    try {
      await updateTrigger({
        triggerId: params.triggerKnowledgeBaseId,
        data: values,
      });
      navigate({ to: "/knowledge-base" });
    } catch (error) {
      console.error("Error updating trigger:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="m-6 max-w-4xl">
        <div className="mb-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="mt-2 h-4 w-96" />
        </div>
        <div className="rounded-lg border bg-card p-6">
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!trigger) {
    return null;
  }

  const defaultValues: TriggerFormValues = {
    title: trigger.title,
    examples: trigger.triggerExamples
      .map((example) => example.example)
      .join("\n"),
    context: trigger.context,
    instances: trigger.instances,
    suggested_actions: trigger.suggested_actions,
    risk_level: trigger.risk_level,
    category: trigger.category,
  };

  return (
    <div className="m-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Edit Trigger</h1>
        <p className="mt-2 text-sm text-gray-500">
          Update the trigger information.
        </p>
      </div>

      <div className="rounded-lg border bg-card p-6">
        <TriggerForm
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          isSubmitting={isUpdating}
        />
      </div>
    </div>
  );
}
